<template>
  <ServicePageLayout
    icon="fas fa-users"
    title="Student Development & Leadership"
    subtitle="Empowering students to become effective leaders and change-makers"
  >
    <!-- Overview -->
    <section class="content-section">
      <h2>
        <i class="fas fa-info-circle section-icon" />
        Overview
      </h2>
      <p>
        The Student Development and Leadership unit is committed to nurturing well-rounded
        individuals through various co-curricular and extra-curricular activities. We provide
        opportunities for students to develop leadership skills, engage in meaningful
        organizations, and contribute to the campus community.
      </p>
    </section>

    <!-- Student Organizations & Activities -->
    <section
      id="organizations"
      class="content-section"
    >
      <h2>
        <i class="fas fa-users section-icon" />
        Student Organizations & Activities
      </h2>
      <p>
        Student organizations are the heart of campus life. We support and supervise various
        academic, interest-based, and service organizations that cater to diverse student
        interests and promote teamwork, collaboration, and personal growth.
      </p>
      <div class="org-categories">
        <div class="org-category">
          <div class="category-icon green">
            <i class="fas fa-book" />
          </div>
          <h3>Academic Organizations</h3>
          <p>Departmental and course-based organizations promoting academic excellence.</p>
        </div>
        <div class="org-category">
          <div class="category-icon yellow">
            <i class="fas fa-palette" />
          </div>
          <h3>Interest-Based Clubs</h3>
          <p>Arts, sports, music, technology, and hobby-related organizations.</p>
        </div>
        <div class="org-category">
          <div class="category-icon green">
            <i class="fas fa-hands-helping" />
          </div>
          <h3>Service Organizations</h3>
          <p>Community service and outreach-focused groups making a difference.</p>
        </div>
        <div class="org-category">
          <div class="category-icon yellow">
            <i class="fas fa-globe" />
          </div>
          <h3>Cultural Organizations</h3>
          <p>Groups celebrating diversity and promoting cultural awareness.</p>
        </div>
      </div>
      <div class="org-stats">
        <div class="stat">
          <span class="stat-number">30+</span>
          <span class="stat-label">Recognized Organizations</span>
        </div>
        <div class="stat">
          <span class="stat-number">2,000+</span>
          <span class="stat-label">Active Members</span>
        </div>
        <div class="stat">
          <span class="stat-number">50+</span>
          <span class="stat-label">Events Annually</span>
        </div>
      </div>
    </section>

    <section
      id="leadership"
      class="content-section"
    >
      <h2>
        <i class="fas fa-award section-icon" />
        Leadership Training Programs
      </h2>
      <p>
        We offer comprehensive leadership development programs designed to equip students
        with the skills and knowledge needed to lead effectively in various contexts.
      </p>
      <div class="programs-grid">
        <div class="program-card">
          <div class="program-badge">
            <i class="fas fa-star" />
          </div>
          <h3>Student Leaders Summit</h3>
          <p>Annual gathering of student leaders for workshops, networking, and planning.</p>
          <span class="program-frequency">Annually</span>
        </div>
        <div class="program-card">
          <div class="program-badge yellow">
            <i class="fas fa-chalkboard-teacher" />
          </div>
          <h3>Leadership Seminars</h3>
          <p>Regular seminars on various leadership topics and skills development.</p>
          <span class="program-frequency">Monthly</span>
        </div>
        <div class="program-card">
          <div class="program-badge">
            <i class="fas fa-tasks" />
          </div>
          <h3>Project Management Training</h3>
          <p>Practical training on planning and executing organizational activities.</p>
          <span class="program-frequency">Semestral</span>
        </div>
        <div class="program-card">
          <div class="program-badge yellow">
            <i class="fas fa-comments" />
          </div>
          <h3>Communication Skills Workshop</h3>
          <p>Enhancing public speaking, presentation, and interpersonal skills.</p>
          <span class="program-frequency">Quarterly</span>
        </div>
      </div>
    </section>

    <!-- Student Council / Government -->
    <section
      id="student-council"
      class="content-section highlight"
    >
      <h2>
        <i class="fas fa-landmark section-icon" />
        Student Council / Government
      </h2>
      <p>
        The Supreme Student Council (SSC) serves as the highest governing body of the
        student population. It represents student interests, advocates for student welfare,
        and organizes campus-wide activities and programs.
      </p>
      <div class="council-info">
        <div class="council-card">
          <h4>Executive Officers</h4>
          <ul>
            <li>President</li>
            <li>Vice President for Internal Affairs</li>
            <li>Vice President for External Affairs</li>
            <li>Secretary General</li>
            <li>Treasurer</li>
            <li>Auditor</li>
            <li>Public Relations Officer</li>
          </ul>
        </div>
        <div class="council-card">
          <h4>Committees</h4>
          <ul>
            <li>Academic Affairs</li>
            <li>Student Welfare</li>
            <li>Sports & Recreation</li>
            <li>Cultural Affairs</li>
            <li>Ways & Means</li>
            <li>Environmental Affairs</li>
          </ul>
        </div>
      </div>
      <div class="election-info">
        <i class="fas fa-vote-yea" />
        <p>
          <strong>SSC Elections:</strong> Held annually during the second semester. All
          bonafide students are eligible to vote and run for positions.
        </p>
      </div>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-plus-circle section-icon" />
        How to Start an Organization
      </h2>
      <div class="steps-list">
        <div class="step">
          <div class="step-number">
            1
          </div>
          <div class="step-content">
            <h4>Gather Members</h4>
            <p>Recruit at least 15 bonafide students who share your interest or cause.</p>
          </div>
        </div>
        <div class="step">
          <div class="step-number">
            2
          </div>
          <div class="step-content">
            <h4>Draft Constitution & By-Laws</h4>
            <p>Create your organization's governing document outlining purpose, structure, and rules.</p>
          </div>
        </div>
        <div class="step">
          <div class="step-number">
            3
          </div>
          <div class="step-content">
            <h4>Find a Faculty Adviser</h4>
            <p>Secure a faculty member willing to guide and supervise your organization.</p>
          </div>
        </div>
        <div class="step">
          <div class="step-number">
            4
          </div>
          <div class="step-content">
            <h4>Submit Application</h4>
            <p>File your application with OSAS for recognition and approval.</p>
          </div>
        </div>
      </div>
      <button
        class="apply-btn"
        @click="applyOrganization"
      >
        <i class="fas fa-file-alt" />
        Download Application Form
      </button>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-phone section-icon" />
        Contact Student Development
      </h2>
      <div class="contact-info">
        <div class="contact-item">
          <i class="fas fa-map-marker-alt" />
          <span>Room 203, Student Services Building</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-phone" />
          <span>(078) 123-4567 Local 203</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-envelope" />
          <span>development@nc.edu.ph</span>
        </div>
      </div>
    </section>
  </ServicePageLayout>
</template>

<script>
import ServicePageLayout from '@/components/landing/ServicePageLayout.vue'

export default {
  name: 'LeadershipService',

  components: {
    ServicePageLayout
  },

  metaInfo: () => ({
    title: 'Student Development & Leadership'
  }),

  methods: {
    applyOrganization () {
      this.$bvToast.toast('Organization application form download coming soon!', {
        title: 'Coming Soon',
        variant: 'info',
        solid: true
      })
    }
  }
}
</script>
